import { useState } from "react";
import { Phone, Mail, MapPin, Send, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { CONTACT } from "@/lib/assets";
import { API_BASE } from "@/lib/api";

const EMPTY_FORM = { name: "", email: "", phone: "", message: "" };

function useContactForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", msg: "" });
    try {
      await axios.post(`${API_BASE}/contact`, form);
      setStatus({
        state: "ok",
        msg: "Takk! Vi har mottatt forespørselen og tar kontakt så snart som mulig.",
      });
      setForm(EMPTY_FORM);
    } catch (err) {
      const d = err.response?.data?.detail;
      setStatus({
        state: "err",
        msg:
          typeof d === "string"
            ? d
            : "Noe gikk galt – ring oss gjerne direkte på " + CONTACT.phone,
      });
    }
  };

  return { form, status, handleChange, handleSubmit, sending: status.state === "sending" };
}

export default function Contact() {
  const { form, status, handleChange, handleSubmit, sending } = useContactForm();

  return (
    <section id="kontakt" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-coral/15 blur-[150px]" />
      <div className="absolute -bottom-32 left-0 w-[500px] h-[500px] rounded-full bg-cyan/15 blur-[150px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: info */}
          <ContactInfo />

          {/* Right: form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={handleSubmit}
              className="glass rounded-3xl p-7 sm:p-10 grain"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field label="Navn" name="name" value={form.name} onChange={handleChange} required />
                <Field label="Telefon" name="phone" type="tel" value={form.phone} onChange={handleChange} required />
              </div>
              <div className="mb-5">
                <Field label="E-post" name="email" type="email" value={form.email} onChange={handleChange} required />
              </div>
              <div className="mb-7">
                <label className="block mono text-[10px] uppercase tracking-[0.22em] text-white/60 mb-2">
                  Melding
                </label>
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  required
                  placeholder="Fortell kort hva du ønsker hjelp med…"
                  className="w-full bg-transparent border hairline rounded-xl px-4 py-3 text-white placeholder:text-white/35 focus:border-cyan focus:outline-none transition"
                  data-testid="contact-message"
                />
              </div>

              {status.state === "ok" && (
                <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-cyan/10 border border-cyan/30 text-cyan-soft" data-testid="contact-success">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-cyan" />
                  <span className="text-sm leading-relaxed">{status.msg}</span>
                </div>
              )}
              {status.state === "err" && (
                <div className="mb-5 p-4 rounded-xl bg-coral/10 border border-coral/30 text-coral text-sm" data-testid="contact-error">
                  {status.msg}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-white/45 max-w-xs">
                  Vi svarer normalt innen 1 virkedag. Ringer du, er det enda raskere.
                </p>
                <button type="submit" disabled={sending} className="btn btn-primary" data-testid="contact-submit">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Sender…" : "Send forespørsel"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <div className="lg:col-span-5">
      <div className="mono text-[11px] uppercase tracking-[0.3em] text-coral mb-5 reveal">
        / Kontakt
      </div>
      <h2 className="reveal text-4xl sm:text-5xl lg:text-[58px] font-bold text-white leading-[1.02] mb-7" data-testid="contact-headline">
        Klar for en
        <br />
        <span className="text-gradient">gratis befaring?</span>
      </h2>
      <p className="reveal text-white/70 text-lg mb-10 max-w-md">
        Fortell oss kort hva du tenker på. Vi tar en uforpliktende prat
        og kommer hjem til deg for å se på prosjektet.
      </p>

      <div className="space-y-4">
        <a
          href={`tel:${CONTACT.phoneRaw}`}
          className="reveal flex items-center gap-4 p-5 rounded-2xl border hairline bg-[hsl(var(--hs-bg-2))] hover:border-cyan/40 transition-all group"
          data-testid="contact-phone"
        >
          <div className="w-12 h-12 rounded-xl bg-cyan/10 text-cyan flex items-center justify-center group-hover:glow-cyan transition">
            <Phone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/50">Telefon</div>
            <div className="text-white font-semibold text-lg">{CONTACT.phone}</div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-cyan transition" />
        </a>

        <a
          href={`mailto:${CONTACT.email}`}
          className="reveal flex items-center gap-4 p-5 rounded-2xl border hairline bg-[hsl(var(--hs-bg-2))] hover:border-coral/40 transition-all group"
          data-testid="contact-email"
        >
          <div className="w-12 h-12 rounded-xl bg-coral/10 text-coral flex items-center justify-center group-hover:glow-coral transition">
            <Mail className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/50">E-post</div>
            <div className="text-white font-semibold">{CONTACT.email}</div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-coral transition" />
        </a>

        <div className="reveal flex items-center gap-4 p-5 rounded-2xl border hairline bg-[hsl(var(--hs-bg-2))]" data-testid="contact-address">
          <div className="w-12 h-12 rounded-xl bg-white/5 text-white/70 flex items-center justify-center">
            <MapPin className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/50">Adresse</div>
            <div className="text-white font-semibold">{CONTACT.address}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block mono text-[10px] uppercase tracking-[0.22em] text-white/60 mb-2" htmlFor={name}>
        {label}{required && <span className="text-coral"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`contact-field-${name}`}
        className="w-full bg-transparent border hairline rounded-xl px-4 py-3 text-white placeholder:text-white/35 focus:border-cyan focus:outline-none transition"
      />
    </div>
  );
}
